import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, DatePicker, Tag, message, Divider, Row, Col, Card } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined, ClearOutlined, FilterOutlined, UserAddOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { equipeService, modalidadeService, usuarioService } from '../services/api';
import dayjs from 'dayjs';
import { formatCPF, formatCNPJ, onlyDigits } from '../utils/documento';

const EquipesList = () => {
  const [equipes, setEquipes] = useState([]);
  const [equipesFiltradas, setEquipesFiltradas] = useState([]);
  const [modalidades, setModalidades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isVinculoModalVisible, setIsVinculoModalVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [vinculoEquipe, setVinculoEquipe] = useState(null);
  const [usuariosEncontrados, setUsuariosEncontrados] = useState([]);
  const [usuariosLoading, setUsuariosLoading] = useState(false);
  const [form] = Form.useForm();
  const [vinculoForm] = Form.useForm();
  const [filtros, setFiltros] = useState({
    busca_geral: '',
    cargo: null,
    status_contrato: null,
    modalidades_ids: [],
  });

  const compareText = (a, b) =>
    (a || '').toString().localeCompare((b || '').toString(), 'pt-BR', { sensitivity: 'base' });

  const getEquipeModalidadesIds = (equipe) => {
    if (Array.isArray(equipe?.modalidades_ids) && equipe.modalidades_ids.length > 0) {
      return equipe.modalidades_ids;
    }

    if (Array.isArray(equipe?.modalidades) && equipe.modalidades.length > 0) {
      return equipe.modalidades.map((m) => m.id).filter(Boolean);
    }

    return [];
  };

  useEffect(() => {
    fetchEquipes();
    fetchModalidades();
  }, []);

  const aplicarFiltros = useCallback(() => {
    let resultado = [...equipes];

    if (filtros.cargo) {
      resultado = resultado.filter(e => e.cargo === filtros.cargo);
    }

    if (filtros.status_contrato) {
      resultado = resultado.filter(e => e.status === filtros.status_contrato);
    }

    if (Array.isArray(filtros.modalidades_ids) && filtros.modalidades_ids.length > 0) {
      resultado = resultado.filter((equipe) => {
        const equipeModalidades = getEquipeModalidadesIds(equipe);
        return filtros.modalidades_ids.some((idModalidade) => equipeModalidades.includes(idModalidade));
      });
    }

    if (filtros.busca_geral) {
      const termo = filtros.busca_geral.toLowerCase();
      resultado = resultado.filter(e => {
        const nome = e.nome ? e.nome.toLowerCase() : '';
        const email = e.email ? e.email.toLowerCase() : '';
        const cpf = e.cpf ? e.cpf : '';
        const telefone = e.telefone ? e.telefone : '';
        return nome.includes(termo) || email.includes(termo) || 
               cpf.includes(termo) || telefone.includes(termo);
      });
    }

    setEquipesFiltradas(resultado);
  }, [equipes, filtros]);

  useEffect(() => {
    aplicarFiltros();
  }, [aplicarFiltros]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limparFiltros = () => {
    setFiltros({ busca_geral: '', cargo: null, status_contrato: null, modalidades_ids: [] });
    message.success('Filtros limpos');
  };

  const getCargosUnicos = () => {
    const cargos = [...new Set(equipes.map(e => e.cargo).filter(Boolean))];
    return cargos.sort();
  };

  const fetchEquipes = async () => {
    setLoading(true);
    try {
      const response = await equipeService.list();
      setEquipes(response.data);
    } catch (error) {
      message.error('Erro ao carregar equipes');
      console.error(error);
    }
    setLoading(false);
  };

  const fetchModalidades = async () => {
    try {
      const response = await modalidadeService.list();
      setModalidades(response.data);
    } catch (error) {
      console.error('Erro ao carregar modalidades');
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setEditingId(null);
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    const formData = {
      ...record,
      dt_nasc: record.dt_nasc ? dayjs(record.dt_nasc) : null,
      inicio_contrato: record.inicio_contrato ? dayjs(record.inicio_contrato) : null,
      fim_contrato: record.fim_contrato ? dayjs(record.fim_contrato) : null,
      modalidades_ids: Array.isArray(record.modalidades_ids)
        ? record.modalidades_ids
        : Array.isArray(record.modalidades)
          ? record.modalidades.map(m => m.id)
          : [],
    };
    form.setFieldsValue(formData);
    setIsModalVisible(true);
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirmação',
      content: 'Deseja excluir este membro da equipe?',
      okText: 'Sim',
      cancelText: 'Não',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await equipeService.delete(id);
          message.success('Membro da equipe excluído com sucesso');
          fetchEquipes();
        } catch (error) {
          message.error('Erro ao excluir membro da equipe');
        }
      },
    });
  };

  const handleVincularUsuario = (record) => {
    setVinculoEquipe(record);
    vinculoForm.resetFields();
    setUsuariosEncontrados([]);
    setIsVinculoModalVisible(true);
  };

  const handleBuscarUsuarios = async (termo) => {
    const busca = termo ? termo.trim() : '';
    if (!busca) {
      setUsuariosEncontrados([]);
      return;
    }

    setUsuariosLoading(true);
    try {
      const response = await usuarioService.buscar(busca, 20);
      setUsuariosEncontrados(response.data || []);
    } catch (error) {
      console.error(error);
      setUsuariosEncontrados([]);
    } finally {
      setUsuariosLoading(false);
    }
  };

  const handleSubmitVinculo = async (values) => {
    if (!vinculoEquipe) {
      return;
    }

    const payload = {
      id_usuario: values.id_usuario,
    };

    try {
      await equipeService.vincularUsuario(vinculoEquipe.id, payload);
      message.success('Usuário vinculado e promovido para PROFESSOR');
      setIsVinculoModalVisible(false);
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message;
      message.error(detail || 'Erro ao vincular usuário');
      console.error(error);
    }
  };

  const handleDesvincularUsuario = (record) => {
    Modal.confirm({
      title: 'Desvincular usuario',
      content: 'Deseja remover o vinculo e reverter para RESPONSAVEL?',
      okText: 'Desvincular',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await equipeService.desvincularUsuario(record.id);
          message.success('Usuário desvinculado com sucesso');
        } catch (error) {
          const detail = error?.response?.data?.detail || error?.message;
          message.error(detail || 'Erro ao desvincular usuário');
          console.error(error);
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      const submitData = {
        ...values,
        cpf: onlyDigits(values.cpf).slice(0, 11),
        cnpj: values.cnpj ? onlyDigits(values.cnpj).slice(0, 14) : null,
        salario_fixo:
          values.salario_fixo === '' ? null : values.salario_fixo,
        dt_nasc: values.dt_nasc ? values.dt_nasc.format('YYYY-MM-DD') : null,
        inicio_contrato: values.inicio_contrato ? values.inicio_contrato.format('YYYY-MM-DD') : null,
        fim_contrato: values.fim_contrato ? values.fim_contrato.format('YYYY-MM-DD') : null,
        cep: values.cep ? values.cep.replace(/\D/g, '') : null, // Remove não-dígitos
      };

      if (editingId) {
        await equipeService.update(editingId, submitData);
        message.success('Membro da equipe atualizado com sucesso');
      } else {
        await equipeService.create(submitData);
        message.success('Membro da equipe criado com sucesso');
      }
      setIsModalVisible(false);
      fetchEquipes();
    } catch (error) {
      message.error('Erro ao salvar membro da equipe');
      console.error(error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ATIVO: 'green',
      INATIVO: 'red',
      LICENCA: 'orange',
      DESLIGADO: 'gray',
    };
    return colors[status] || 'default';
  };

  const getModalidadesDisplay = (record) => {
    if (Array.isArray(record?.modalidades) && record.modalidades.length > 0) {
      return record.modalidades.map((m) => m.nome_modalidade).join(', ');
    }

    if (Array.isArray(record?.modalidades_ids) && record.modalidades_ids.length > 0) {
      const nomesModalidades = record.modalidades_ids
        .map((id) => modalidades.find((m) => m.id === id)?.nome_modalidade)
        .filter(Boolean);
      return nomesModalidades.length > 0 ? nomesModalidades.join(', ') : '-';
    }

    return '-';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      sorter: (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0),
    },
    {
      title: 'Nome',
      dataIndex: 'nome',
      key: 'nome',
      sorter: (a, b) => compareText(a.nome, b.nome),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Modalidades',
      dataIndex: 'modalidades',
      key: 'modalidades',
      render: (_, record) => getModalidadesDisplay(record),
      sorter: (a, b) => compareText(getModalidadesDisplay(a), getModalidadesDisplay(b)),
    },
    {
      title: 'Cargo',
      dataIndex: 'cargo',
      key: 'cargo',
    },
    {
      title: 'Valor da Hora Aula',
      dataIndex: 'valor_hora_aula',
      key: 'valor_hora_aula',
      render: (valor) =>
        valor === null || valor === undefined
          ? '-'
          : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text) => (
        <Tag color={getStatusColor(text)}>{text}</Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          {!record?.has_vinculo_usuario && (
            <Button
              icon={<UserAddOutlined />}
              onClick={() => handleVincularUsuario(record)}
              size="small"
            />
          )}
          {record?.has_vinculo_usuario && (
            <Button
              icon={<UserDeleteOutlined />}
              onClick={() => handleDesvincularUsuario(record)}
              size="small"
              danger
            />
          )}
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record.id)}
            size="small"
          />
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Painel de Filtros */}
      <Card 
        title={
          <Space>
            <FilterOutlined />
            Filtros de Busca
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#888' }}>
              ({equipesFiltradas.length} de {equipes.length} membros)
            </span>
          </Space>
        }
        extra={
          <Button type="text" size="small" onClick={() => setMostrarFiltros(!mostrarFiltros)}>
            {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {mostrarFiltros && (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Busca Geral</div>
              <Input
                placeholder="Pesquisar por nome, email, CPF, telefone..."
                prefix={<SearchOutlined />}
                value={filtros.busca_geral}
                onChange={(e) => handleFiltroChange('busca_geral', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Cargo</div>
              <Select
                placeholder="Todos os cargos"
                value={filtros.cargo}
                onChange={(value) => handleFiltroChange('cargo', value)}
                allowClear
                style={{ width: '100%' }}
              >
                {getCargosUnicos().map(cargo => (
                  <Select.Option key={cargo} value={cargo}>{cargo}</Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Status</div>
              <Select
                placeholder="Todos os status"
                value={filtros.status_contrato}
                onChange={(value) => handleFiltroChange('status_contrato', value)}
                allowClear
                style={{ width: '100%' }}
              >
                <Select.Option value="ATIVO">Ativo</Select.Option>
                <Select.Option value="INATIVO">Inativo</Select.Option>
                <Select.Option value="LICENCA">Licença</Select.Option>
                <Select.Option value="DESLIGADO">Desligado</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>Modalidades</div>
              <Select
                mode="multiple"
                placeholder="Todas as modalidades"
                value={filtros.modalidades_ids}
                onChange={(value) => handleFiltroChange('modalidades_ids', value || [])}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: '100%' }}
              >
                {modalidades.map((modalidade) => (
                  <Select.Option key={modalidade.id} value={modalidade.id}>
                    {modalidade.nome_modalidade}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: 4, fontWeight: 500 }}>&nbsp;</div>
              <Button icon={<ClearOutlined />} onClick={limparFiltros} block>
                Limpar Filtros
              </Button>
            </Col>
          </Row>
        )}
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Novo Membro
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={equipesFiltradas}
        loading={loading}
        rowKey="id"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total de ${total} membros`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingId ? 'Editar Membro da Equipe' : 'Novo Membro da Equipe'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Divider>Informações Básicas</Divider>

          <Form.Item
            name="nome"
            label="Nome Completo"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Email é obrigatório' },
                  { type: 'email', message: 'Email inválido' },
                ]}
              >
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="cpf"
                label="CPF"
                rules={[
                  { required: true, message: 'CPF é obrigatório' },
                  { len: 11, message: 'CPF deve ter 11 dígitos' },
                ]}
                getValueFromEvent={(e) => onlyDigits(e?.target?.value).slice(0, 11)}
                getValueProps={(value) => ({ value: formatCPF(value) })}
              >
                <Input placeholder="000.000.000-00" maxLength={14} inputMode="numeric" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="dt_nasc" label="Data de Nascimento">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="whatsapp" label="WhatsApp">
                <Input placeholder="(11) 99999-9999" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Informações Profissionais</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="modalidades_ids"
                label="Modalidades"
                rules={[{ required: false }]}
              >
                <Select 
                  mode="multiple" 
                  placeholder="Selecione uma ou mais modalidades"
                >
                  {modalidades.map((modalidade) => (
                    <Select.Option key={modalidade.id} value={modalidade.id}>
                      {modalidade.nome_modalidade}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="funcao" label="Função">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="cargo" label="Cargo">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="status" label="Status" initialValue="ATIVO">
                <Select>
                  <Select.Option value="ATIVO">Ativo</Select.Option>
                  <Select.Option value="INATIVO">Inativo</Select.Option>
                  <Select.Option value="LICENCA">Licença</Select.Option>
                  <Select.Option value="DESLIGADO">Desligado</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="tipo_contrato" label="Tipo de Contrato">
                <Select placeholder="Selecione">
                  <Select.Option value="CLT">CLT</Select.Option>
                  <Select.Option value="PJ">PJ</Select.Option>
                  <Select.Option value="TEMPORARIO">Temporário</Select.Option>
                  <Select.Option value="PRESTADOR">Prestador de Serviços</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="tipo_remuneracao"
                label="Tipo de Remuneracao"
                initialValue="HORISTA"
                rules={[{ required: true, message: 'Tipo de remuneracao e obrigatorio' }]}
              >
                <Select placeholder="Selecione">
                  <Select.Option value="HORISTA">Horista</Select.Option>
                  <Select.Option value="MENSAL">Mensal</Select.Option>
                  <Select.Option value="MISTO">Misto</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="valor_hora_aula"
                label="Valor Hora/Aula (R$)"
                dependencies={['tipo_remuneracao']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const tipoRemuneracao = getFieldValue('tipo_remuneracao') || 'HORISTA';
                      const preenchido = value !== undefined && value !== null && value !== '';
                      if (tipoRemuneracao === 'HORISTA' && !preenchido) {
                        return Promise.reject(new Error('Valor Hora/Aula e obrigatorio para remuneracao HORISTA'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type="number" step="0.01" placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Endereço</Divider>

          <Form.Item name="endereco" label="Endereço">
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="bairro" label="Bairro">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="cidade" label="Cidade">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="cep" label="CEP">
                <Input placeholder="00000-000" />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Informações Financeiras</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="forma_recebimento" label="Forma de Recebimento">
                <Select placeholder="Selecione">
                  <Select.Option value="TRANSFERENCIA">Transferência Bancária</Select.Option>
                  <Select.Option value="PIX">PIX</Select.Option>
                  <Select.Option value="DINHEIRO">Dinheiro</Select.Option>
                  <Select.Option value="CHEQUE">Cheque</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="chave_pix" label="Chave PIX">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="banco" label="Banco">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="agencia" label="Agência">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="conta" label="Conta">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="tipo_conta" label="Tipo de Conta">
                <Select placeholder="Selecione">
                  <Select.Option value="CORRENTE">Corrente</Select.Option>
                  <Select.Option value="POUPANCA">Poupança</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="cnpj"
                label="CNPJ (se aplicável)"
                getValueFromEvent={(e) => onlyDigits(e?.target?.value).slice(0, 14)}
                getValueProps={(value) => ({ value: formatCNPJ(value) })}
              >
                <Input placeholder="00.000.000/0000-00" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="salario_fixo"
                label="Salario Fixo (R$)"
                dependencies={['tipo_remuneracao']}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const tipoRemuneracao = getFieldValue('tipo_remuneracao') || 'HORISTA';
                      const preenchido = value !== undefined && value !== null && value !== '';
                      if (tipoRemuneracao === 'MENSAL' && !preenchido) {
                        return Promise.reject(new Error('Salario Fixo e obrigatorio para remuneracao MENSAL'));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input type="number" step="0.01" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="ajuda_custo" label="Ajuda de Custo (R$)">
                <Input type="number" step="0.01" placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="custo_mensal_estimado" label="Custo Mensal Estimado (R$)">
            <Input type="number" step="0.01" placeholder="0.00" />
          </Form.Item>

          <Divider>Datas do Contrato</Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="inicio_contrato" label="Início do Contrato">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="fim_contrato" label="Fim do Contrato">
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider>Observações</Divider>

          <Form.Item name="obs" label="Observações">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Vincular usuario a equipe"
        open={isVinculoModalVisible}
        onOk={() => vinculoForm.submit()}
        onCancel={() => setIsVinculoModalVisible(false)}
        okText="Vincular"
        cancelText="Cancelar"
      >
        <p>
          Este usuario sera promovido para <strong>PROFESSOR</strong> no proximo login.
        </p>
        <Form
          form={vinculoForm}
          layout="vertical"
          onFinish={handleSubmitVinculo}
        >
          <Form.Item
            name="id_usuario"
            label="Usuario"
            rules={[{ required: true, message: 'Selecione um usuario' }]}
          >
            <Select
              showSearch
              placeholder="Buscar por nome, email ou CPF"
              onSearch={handleBuscarUsuarios}
              filterOption={false}
              loading={usuariosLoading}
              notFoundContent={usuariosLoading ? 'Buscando...' : 'Nenhum usuario encontrado'}
            >
              {usuariosEncontrados.map((usuario) => (
                <Select.Option key={usuario.id} value={usuario.id}>
                  {usuario.nome_completo} - {usuario.email}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EquipesList;
